import { Fragment, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion, useScroll, useTransform } from 'framer-motion';
import {
  Users, GraduationCap, BarChart3, ShieldCheck, Megaphone, Handshake, Map, ArrowRight,
  TrendingUp, Building2, Newspaper, MapPin, Bot, Globe, FileText, LineChart,
  CheckCircle2, Sparkles, Landmark, PlayCircle,
} from 'lucide-react';
import QRCode from 'react-qr-code';
import Reveal from '../../components/public/Reveal';
import Counter from '../../components/public/Counter';
import Marquee from '../../components/public/Marquee';
import PhoneMockup from '../../components/public/PhoneMockup';
import ShortsReel from '../../components/public/ShortsReel';
import { useSiteConfig } from '../../lib/siteConfig';
import api from '../../lib/api';

const ICONS = {
  Users, GraduationCap, BarChart3, ShieldCheck, Megaphone, Handshake, Map, Newspaper,
  MapPin, Bot, Globe, Building2, FileText, LineChart, TrendingUp, Sparkles, Landmark,
};

function scrollToConnect() {
  document.getElementById('connect')?.scrollIntoView({ behavior: 'smooth' });
}

function Hero({ cfg }) {
  const { scrollY } = useScroll();
  const bgY = useTransform(scrollY, [0, 900], [0, 220]);
  const h = cfg.hero;
  const colorFor = (i) => (i === 1 ? 'text-orange-500' : 'text-white');

  return (
    <section className="hero-grain relative min-h-[100svh] overflow-hidden bg-cnib-navy" data-testid="hero-section">
      <motion.div style={{ y: bgY }} className="absolute inset-0">
        <img src={h.bg_image} alt="Skyline urbano" className="h-[120%] w-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-r from-cnib-navy/95 via-cnib-navy/55 to-cnib-navy/10" />
        <div className="absolute inset-0 bg-gradient-to-t from-cnib-navy via-transparent to-cnib-navy/20" />
      </motion.div>

      <div className="relative z-10 mx-auto grid max-w-7xl items-center gap-14 px-4 pb-48 pt-32 sm:px-6 md:pt-40 lg:grid-cols-2 lg:px-8">
        <div>
          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-xs font-bold uppercase tracking-[0.3em] text-orange-500"
          >
            {h.eyebrow}
          </motion.p>

          <div className="grid grid-cols-[1fr_auto] items-start gap-3 xs:gap-5 sm:gap-8 lg:block">
            <h1
              data-testid="hero-title"
              className="mt-5 font-heading text-[1.75rem] font-extrabold leading-[1.08] tracking-tight xs:text-3xl sm:text-5xl lg:text-6xl"
            >
              {h.lines.map((line, i) => (
                <span key={i} className="block overflow-hidden pb-1">
                  <motion.span
                    className={`block ${colorFor(i)}`}
                    initial={{ y: '115%' }}
                    animate={{ y: 0 }}
                    transition={{ duration: 0.9, delay: 0.15 + i * 0.15, ease: [0.22, 1, 0.36, 1] }}
                  >
                    {line}
                  </motion.span>
                </span>
              ))}
            </h1>

            <motion.div
              initial={{ opacity: 0, y: 40, rotate: 3 }}
              animate={{ opacity: 1, y: 0, rotate: 0 }}
              transition={{ duration: 0.9, delay: 0.5, ease: [0.22, 1, 0.36, 1] }}
              className="mt-6 mr-6 flex justify-end xs:mr-8 sm:mr-14 lg:hidden"
            >
              <PhoneMockup mini mapImg={h.phone_map} propertyImg={h.phone_property} />
            </motion.div>
          </div>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.7 }}
            className="mt-6 max-w-md text-lg leading-relaxed text-slate-300"
          >
            {h.subtitle}
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.85 }}
            className="mt-9 flex flex-wrap items-center gap-4"
          >
            <Link
              to="/hagase-socio"
              data-testid="hero-cta-hagase-socio-button"
              className="group flex items-center gap-2 rounded-xl bg-orange-500 px-6 py-3 text-sm font-bold text-white shadow-md shadow-orange-500/25 transition-all duration-300 hover:-translate-y-0.5 hover:bg-orange-600 hover:shadow-lg hover:shadow-orange-600/30"
            >
              {h.cta_primary}
              <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
            </Link>
            <button
              onClick={scrollToConnect}
              data-testid="hero-cta-explorar-button"
              className="group flex items-center gap-3 rounded-xl border-2 border-white/40 bg-white/5 px-6 py-[10px] text-sm font-semibold text-white backdrop-blur-sm transition-all duration-300 hover:border-white hover:bg-white/10"
            >
              {h.cta_secondary}
              <span className="flex h-7 w-7 items-center justify-center rounded-full border-2 border-white/60 transition-all duration-300 group-hover:border-orange-500 group-hover:bg-orange-500">
                <PlayCircle className="h-3.5 w-3.5" />
              </span>
            </button>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.8, delay: 1.1 }}
            className="mt-12 grid gap-4 sm:grid-cols-3"
          >
            {h.features.map((f, i) => {
              const Icon = ICONS[f.icon] || TrendingUp;
              return (
                <div key={i} className="flex items-start gap-3">
                  <Icon className="mt-0.5 h-5 w-5 shrink-0 text-orange-500" />
                  <p className="text-sm leading-snug text-slate-300">{f.text}</p>
                </div>
              );
            })}
          </motion.div>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 60, rotate: 3 }}
          animate={{ opacity: 1, y: 0, rotate: 0 }}
          transition={{ duration: 1, delay: 0.5, ease: [0.22, 1, 0.36, 1] }}
          className="hidden justify-center pr-24 lg:flex lg:pr-36"
        >
          <PhoneMockup big mapImg={h.phone_map} propertyImg={h.phone_property} />
        </motion.div>
      </div>
    </section>
  );
}

function StatsBar({ cfg }) {
  return (
    <section className="relative z-20 mx-auto -mt-28 max-w-7xl px-4 sm:px-6 lg:px-8" data-testid="stats-bar-container">
      <Reveal>
        <div className="grid grid-cols-2 gap-6 rounded-3xl border border-slate-100 bg-white p-6 shadow-2xl shadow-cnib-navy/10 md:grid-cols-4 md:p-8">
          {cfg.stats.map((stat) => {
            const Icon = ICONS[stat.icon] || Users;
            return (
              <div key={stat.label} className="flex flex-col items-center gap-2 text-center">
                <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-cnib-blue/10">
                  <Icon className="h-6 w-6 text-cnib-blue" />
                </div>
                <p className="font-heading text-3xl font-extrabold text-orange-500 md:text-4xl">
                  <Counter to={stat.value} prefix={stat.prefix} />
                </p>
                <p className="text-xs font-semibold uppercase tracking-wide text-slate-500 md:text-sm">{stat.label}</p>
              </div>
            );
          })}
        </div>
      </Reveal>
    </section>
  );
}

function Benefits({ cfg }) {
  const b = cfg.benefits;
  return (
    <section className="py-20 md:py-28">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="grid items-end gap-6 md:grid-cols-2">
          <Reveal>
            <p className="text-xs font-bold uppercase tracking-[0.25em] text-orange-500">{b.eyebrow}</p>
            <h2 className="mt-4 font-heading text-3xl font-bold tracking-tight text-slate-900 sm:text-4xl">{b.title}</h2>
          </Reveal>
          <Reveal delay={0.15}>
            <p className="text-base leading-relaxed text-slate-600 md:text-lg">{b.description}</p>
          </Reveal>
        </div>

        <div className="mt-14 grid gap-6 sm:grid-cols-2 lg:grid-cols-5">
          {b.items.map((item, i) => {
            const Icon = ICONS[item.icon] || Users;
            return (
              <Reveal key={i} delay={i * 0.08}>
                <div className="group h-full rounded-2xl border border-slate-100 bg-white p-6 transition-all duration-300 hover:-translate-y-1 hover:border-cnib-blue/20 hover:shadow-xl">
                  <div className="flex h-14 w-14 items-center justify-center rounded-2xl border-2 border-cnib-blue/15 transition-colors duration-300 group-hover:border-orange-500/40 group-hover:bg-orange-50">
                    <Icon className="h-7 w-7 text-cnib-blue transition-colors duration-300 group-hover:text-orange-500" />
                  </div>
                  <h3 className="mt-5 font-heading text-lg font-semibold text-slate-900">{item.title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-slate-600">{item.text}</p>
                </div>
              </Reveal>
            );
          })}
        </div>
      </div>
    </section>
  );
}

function Connect() {
  const [ev, setEv] = useState(null);
  const [loaded, setLoaded] = useState(false);
  const [tick, setTick] = useState(0);

  useEffect(() => {
    api.get('/events')
      .then(({ data }) => {
        const now = Date.now();
        const upcoming = (data || [])
          .filter((e) => e.starts_at && new Date(e.starts_at).getTime() > now)
          .sort((a, b) => new Date(a.starts_at) - new Date(b.starts_at));
        setEv(upcoming[0] || null);
      })
      .catch(() => {})
      .finally(() => setLoaded(true));
  }, []);

  useEffect(() => {
    const id = setInterval(() => setTick((t) => t + 1), 1000);
    return () => clearInterval(id);
  }, []);

  if (!loaded || !ev) return null;

  const diffMs = Math.max(0, new Date(ev.starts_at).getTime() - Date.now());
  const days = Math.floor(diffMs / 86400000);
  const hours = Math.floor((diffMs % 86400000) / 3600000);
  const minutes = Math.floor((diffMs % 3600000) / 60000);
  const seconds = Math.floor((diffMs % 60000) / 1000);
  // read tick so React re-renders every second
  void tick;

  const dateLabel = new Date(ev.starts_at).toLocaleDateString('es-BO', {
    weekday: 'long', day: 'numeric', month: 'long', year: 'numeric',
  });

  return (
    <section
      id="connect"
      className="hero-grain relative overflow-hidden bg-cnib-navy py-20 md:py-28"
      data-testid="home-next-event-section"
    >
      <div className="pointer-events-none absolute inset-0">
        {ev.cover_image && (
          <img
            src={ev.cover_image}
            alt=""
            className="absolute inset-0 h-full w-full object-cover opacity-20"
          />
        )}
        <div className="absolute inset-0 bg-gradient-to-b from-cnib-navy/80 via-cnib-navy/95 to-cnib-navy" />
      </div>

      <div className="relative mx-auto grid max-w-7xl items-center gap-10 px-4 sm:px-6 lg:grid-cols-5 lg:px-8">
        <Reveal className="lg:col-span-2">
          <p className="text-xs font-bold uppercase tracking-[0.3em] text-orange-500">Próximo evento</p>
          <h2 className="mt-4 font-heading text-4xl font-extrabold tracking-tight text-white sm:text-5xl">
            {ev.title}
          </h2>
          {ev.subtitle && (
            <p className="mt-3 text-lg text-slate-300">{ev.subtitle}</p>
          )}
          <div className="mt-6 flex flex-wrap gap-3 text-sm text-slate-200">
            <span className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/5 px-4 py-1.5 font-semibold">
              <Sparkles className="h-4 w-4 text-orange-400" />
              {dateLabel}
            </span>
            {ev.city && (
              <span className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/5 px-4 py-1.5 font-semibold">
                <MapPin className="h-4 w-4 text-orange-400" />
                {ev.city}
              </span>
            )}
          </div>
          <div className="mt-8 flex flex-wrap gap-3">
            <Link
              to={`/eventos/${ev.slug}`}
              data-testid="home-next-event-view-button"
              className="group inline-flex items-center gap-2 rounded-full bg-orange-500 px-6 py-3 text-sm font-bold text-white shadow-lg shadow-orange-500/30 transition-all duration-300 hover:-translate-y-0.5 hover:bg-orange-600"
            >
              Ver más del evento
              <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
            </Link>
            <Link
              to="/eventos"
              data-testid="home-next-event-all-link"
              className="inline-flex items-center gap-2 rounded-full border border-white/25 px-5 py-3 text-sm font-semibold text-white transition-colors hover:border-orange-500 hover:text-orange-400"
            >
              Ver todos
            </Link>
          </div>
        </Reveal>

        <Reveal delay={0.15} className="lg:col-span-3">
          <div className="relative overflow-hidden rounded-[32px] border border-white/10 bg-white/5 shadow-2xl backdrop-blur-sm">
            {ev.cover_image ? (
              <div className="relative aspect-[16/10] overflow-hidden">
                <img src={ev.cover_image} alt={ev.title} className="h-full w-full object-cover" />
                <div className="absolute inset-0 bg-gradient-to-t from-cnib-navy/90 via-cnib-navy/20 to-transparent" />
                <span className="absolute left-5 top-5 rounded-full bg-orange-500 px-4 py-1 text-[10px] font-bold uppercase tracking-wider text-white">
                  Faltan {days} {days === 1 ? 'día' : 'días'}
                </span>
              </div>
            ) : (
              <div className="flex aspect-[16/10] items-center justify-center bg-gradient-to-br from-cnib-blue to-cnib-navy">
                <Building2 className="h-20 w-20 text-white/30" />
              </div>
            )}

            <div className="grid grid-cols-4 gap-2 border-t border-white/10 bg-cnib-navy/80 p-5 backdrop-blur md:p-7" data-testid="home-next-event-countdown">
              {[
                { v: days, l: days === 1 ? 'Día' : 'Días' },
                { v: hours, l: 'Hrs' },
                { v: minutes, l: 'Min' },
                { v: seconds, l: 'Seg' },
              ].map((unit, idx) => (
                <div key={idx} className="rounded-2xl bg-white/5 py-3 text-center ring-1 ring-white/10 md:py-4">
                  <p className="font-heading text-3xl font-black leading-none text-white sm:text-4xl md:text-5xl">
                    {String(unit.v).padStart(2, '0')}
                  </p>
                  <p className="mt-1 text-[10px] font-bold uppercase tracking-[0.2em] text-orange-300">
                    {unit.l}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
}

function Modules({ cfg }) {
  const m = cfg.modules;
  const items = m.items.filter((it) => it.visible !== false);
  return (
    <section className="py-20 md:py-28">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="grid items-end gap-6 md:grid-cols-2">
          <Reveal>
            <p className="text-xs font-bold uppercase tracking-[0.25em] text-orange-500">{m.eyebrow}</p>
            <h2 className="mt-4 font-heading text-3xl font-bold tracking-tight text-slate-900 sm:text-4xl">{m.title}</h2>
          </Reveal>
          <Reveal delay={0.15}>
            <p className="text-base leading-relaxed text-slate-600 md:text-lg">{m.description}</p>
          </Reveal>
        </div>

        <div className="mt-14 grid gap-5 sm:grid-cols-2 lg:grid-cols-5" data-testid="bento-grid-container">
          {items.map((mod, i) => {
            const Icon = ICONS[mod.icon] || Building2;
            return (
              <Reveal key={mod.id} delay={(i % 5) * 0.07}>
                <div
                  data-testid={`bento-card-${mod.id}`}
                  className="group flex h-full flex-col rounded-2xl border border-slate-100 bg-white p-6 transition-all duration-300 hover:-translate-y-1 hover:shadow-xl"
                >
                  <div className={`flex h-12 w-12 items-center justify-center rounded-xl bg-gradient-to-br ${mod.color} shadow-lg`}>
                    <Icon className="h-6 w-6 text-white" />
                  </div>
                  <span className="mt-4 text-[10px] font-bold uppercase tracking-[0.2em] text-slate-400">{mod.tag}</span>
                  <h3 className="mt-1.5 font-heading text-base font-bold text-slate-900">{mod.title}</h3>
                  <p className="mt-2 flex-1 text-sm leading-relaxed text-slate-600">{mod.description}</p>
                  <ArrowRight className="mt-4 h-4 w-4 text-slate-300 transition-all duration-300 group-hover:translate-x-1 group-hover:text-orange-500" />
                </div>
              </Reveal>
            );
          })}
        </div>
      </div>
    </section>
  );
}

function CtaBanner({ cfg }) {
  const c = cfg.cta;
  return (
    <section className="mx-auto max-w-7xl px-4 pb-24 sm:px-6 lg:px-8">
      <Reveal>
        <div className="hero-grain relative overflow-hidden rounded-3xl bg-cnib-navy">
          <div className="grid lg:grid-cols-2">
            <div className="relative z-10 p-8 md:p-14">
              <h2 className="font-heading text-3xl font-bold leading-tight text-white sm:text-4xl">{c.title}</h2>
              <div className="mt-10 grid grid-cols-2 gap-5">
                {c.pillars.map((p, i) => {
                  const Icon = ICONS[p.icon] || ShieldCheck;
                  return (
                    <div key={i} className="flex items-center gap-3">
                      <Icon className="h-5 w-5 shrink-0 text-orange-500" />
                      <span className="text-sm font-medium text-slate-300">{p.label}</span>
                    </div>
                  );
                })}
              </div>
            </div>
            <div className="relative min-h-[280px]">
              <img src={c.image} alt="Edificio moderno" className="absolute inset-0 h-full w-full object-cover" />
              <div className="absolute inset-0 bg-gradient-to-r from-cnib-navy via-cnib-navy/60 to-cnib-navy/20" />
              <div className="relative z-10 flex h-full flex-col items-start justify-center p-8 md:p-14">
                <p className="font-heading text-2xl font-bold leading-snug text-white">{c.text}</p>
                <Link
                  to="/hagase-socio"
                  data-testid="cta-banner-button"
                  className="group mt-6 flex items-center gap-2 rounded-xl bg-orange-500 px-6 py-3 text-sm font-bold text-white shadow-md shadow-orange-500/25 transition-all duration-300 hover:-translate-y-0.5 hover:bg-orange-600"
                >
                  {c.button}
                  <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
                </Link>
              </div>
            </div>
          </div>
        </div>
      </Reveal>
    </section>
  );
}

const HIGHLIGHT_ORDER = { urgente: 0, destacada: 1, normal: 2 };

function EventsSection() {
  const [events, setEvents] = useState([]);
  const [loaded, setLoaded] = useState(false);
  useEffect(() => {
    api.get('/events').then(({ data }) => setEvents((data || []).slice(0, 3))).catch(() => {}).finally(() => setLoaded(true));
  }, []);
  if (loaded && events.length === 0) return null;
  const daysUntil = (iso) => Math.max(0, Math.ceil((new Date(iso).getTime() - Date.now()) / 86400000));

  return (
    <section className="bg-slate-50 pb-20 pt-10 md:pb-28" data-testid="home-events-section">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <Reveal>
            <p className="text-xs font-bold uppercase tracking-[0.25em] text-orange-500">Congresos y capacitaciones</p>
            <h2 className="mt-4 font-heading text-3xl font-bold tracking-tight text-slate-900 sm:text-4xl">Próximos eventos CNIB</h2>
            <p className="mt-3 max-w-xl text-base text-slate-600">Reserva tu lugar en los encuentros que están transformando el sector inmobiliario boliviano.</p>
          </Reveal>
          <Reveal delay={0.15}>
            <Link to="/eventos" data-testid="home-events-all-link" className="group flex items-center gap-2 rounded-full border-2 border-cnib-navy/15 px-5 py-2.5 text-sm font-bold text-cnib-navy transition-all hover:border-cnib-blue hover:text-cnib-blue">
              Ver todos
              <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
            </Link>
          </Reveal>
        </div>

        <div className="mt-10 grid gap-6 md:grid-cols-3" data-testid="home-events-grid">
          {events.map((ev, i) => (
            <Reveal key={ev.slug} delay={i * 0.08}>
              <Link to={`/eventos/${ev.slug}`} data-testid={`home-event-card-${ev.slug}`} className="group block h-full overflow-hidden rounded-3xl bg-white shadow-sm transition-all duration-300 hover:-translate-y-1 hover:shadow-xl">
                <div className="relative aspect-[16/10] overflow-hidden bg-slate-100">
                  {ev.cover_image && <img src={ev.cover_image} alt="" className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" />}
                  <div className="absolute inset-0 bg-gradient-to-t from-slate-900/70 via-transparent to-transparent" />
                  <span className="absolute left-3 top-3 rounded-full bg-orange-500 px-3 py-1 text-[10px] font-bold uppercase tracking-wider text-white">
                    En {daysUntil(ev.starts_at)} días
                  </span>
                  <div className="absolute inset-x-0 bottom-0 p-4 text-white">
                    <p className="text-[10px] font-bold uppercase tracking-wider opacity-80">{String(ev.starts_at).slice(0, 10)} · {ev.city}</p>
                  </div>
                </div>
                <div className="p-5">
                  <h3 className="line-clamp-2 font-heading text-lg font-extrabold text-slate-900 group-hover:text-cnib-blue">{ev.title}</h3>
                  {ev.subtitle && <p className="mt-1 line-clamp-2 text-sm text-slate-500">{ev.subtitle}</p>}
                  <span className="mt-3 inline-flex items-center gap-1 text-sm font-bold text-orange-500">
                    Ver evento <ArrowRight className="h-3 w-3 transition-transform group-hover:translate-x-1" />
                  </span>
                </div>
              </Link>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

function NewsSection({ cfg }) {
  const [articles, setArticles] = useState([]);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    api
      .get('/news')
      .then(({ data }) => {
        const sorted = [...data].sort((a, b) => {
          const h = (HIGHLIGHT_ORDER[a.highlight || 'normal'] ?? 2) - (HIGHLIGHT_ORDER[b.highlight || 'normal'] ?? 2);
          return h !== 0 ? h : new Date(b.created_at) - new Date(a.created_at);
        });
        setArticles(sorted.slice(0, 4));
      })
      .catch(() => {})
      .finally(() => setLoaded(true));
  }, []);

  if (loaded && articles.length === 0) return null;
  const [first, ...rest] = articles;
  const s = cfg.news_section || {};

  return (
    <section className="pb-20 md:pb-28" data-testid="home-news-section">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <Reveal>
            <p className="text-xs font-bold uppercase tracking-[0.25em] text-orange-500">{s.eyebrow}</p>
            <h2 className="mt-4 font-heading text-3xl font-bold tracking-tight text-slate-900 sm:text-4xl">{s.title}</h2>
            <p className="mt-3 max-w-xl text-base text-slate-600">{s.description}</p>
          </Reveal>
          <Reveal delay={0.15}>
            <Link to="/noticias" data-testid="home-news-all-link" className="group flex items-center gap-2 rounded-full border-2 border-cnib-navy/15 px-5 py-2.5 text-sm font-bold text-cnib-navy transition-all hover:border-cnib-blue hover:text-cnib-blue">
              Ver todas
              <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
            </Link>
          </Reveal>
        </div>

        {first && (
          <div className="mt-12 grid gap-6 lg:grid-cols-2">
            <Reveal>
              <Link to={`/noticias/${first.slug}`} data-testid="home-news-top" className="group relative block h-full min-h-[320px] overflow-hidden rounded-3xl">
                {first.image_url && <img src={first.image_url} alt={first.title} className="absolute inset-0 h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" />}
                <div className="absolute inset-0 bg-gradient-to-t from-cnib-navy via-cnib-navy/40 to-transparent" />
                <div className="absolute inset-x-0 bottom-0 p-7">
                  <div className="flex items-center gap-2">
                    <span className="rounded-full bg-orange-500 px-3 py-1 text-[10px] font-bold uppercase tracking-wider text-white">{first.category}</span>
                    {first.highlight === 'urgente' && <span className="rounded-full bg-red-500 px-3 py-1 text-[10px] font-bold uppercase text-white">Urgente</span>}
                    {first.highlight === 'destacada' && <span className="rounded-full bg-white/20 px-3 py-1 text-[10px] font-bold uppercase text-white backdrop-blur">Destacada</span>}
                  </div>
                  <h3 className="mt-3 font-heading text-2xl font-extrabold leading-snug text-white sm:text-3xl">{first.title}</h3>
                  {first.excerpt && <p className="mt-2 line-clamp-2 text-sm text-slate-300">{first.excerpt}</p>}
                </div>
              </Link>
            </Reveal>
            <div className="flex flex-col gap-4">
              {rest.map((a, i) => (
                <Reveal key={a.article_id} delay={0.1 + i * 0.08}>
                  <Link to={`/noticias/${a.slug}`} data-testid={`home-news-card-${i}`} className="group flex h-full items-center gap-5 rounded-2xl border border-slate-100 bg-white p-4 shadow-sm transition-all duration-300 hover:-translate-y-0.5 hover:shadow-lg">
                    {a.image_url && <img src={a.image_url} alt="" className="h-24 w-28 shrink-0 rounded-xl object-cover" />}
                    <div className="min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="text-[10px] font-bold uppercase tracking-wider text-orange-500">{a.category}</span>
                        <span className="text-[10px] text-slate-400">{new Date(a.created_at).toLocaleDateString('es-BO', { day: 'numeric', month: 'short' })}</span>
                      </div>
                      <h3 className="mt-1 line-clamp-2 font-heading text-base font-bold leading-snug text-slate-900 transition-colors group-hover:text-cnib-blue">{a.title}</h3>
                    </div>
                  </Link>
                </Reveal>
              ))}
            </div>
          </div>
        )}
      </div>
    </section>
  );
}

function TrainingsSection() {
  const [items, setItems] = useState([]);
  const [loaded, setLoaded] = useState(false);
  useEffect(() => {
    api.get('/trainings/featured').then(({ data }) => setItems(data || [])).catch(() => {}).finally(() => setLoaded(true));
  }, []);
  if (loaded && items.length === 0) return null;
  const MODALITY_LABEL = { presencial: 'Presencial', zoom: 'Vía Zoom', hibrido: 'Híbrido' };

  return (
    <section className="bg-white py-20 md:py-24" data-testid="home-trainings-section">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <Reveal>
            <p className="text-xs font-bold uppercase tracking-[0.25em] text-orange-500">Campus · Certificaciones</p>
            <h2 className="mt-4 font-heading text-3xl font-bold tracking-tight text-slate-900 sm:text-4xl">Capacitaciones destacadas</h2>
            <p className="mt-3 max-w-xl text-base text-slate-600">Programas y certificaciones internacionales para elevar tu práctica profesional.</p>
          </Reveal>
          <Reveal delay={0.15}>
            <Link to="/capacitacion" data-testid="home-trainings-all-link" className="group flex items-center gap-2 rounded-full border-2 border-cnib-navy/15 px-5 py-2.5 text-sm font-bold text-cnib-navy transition-all hover:border-cnib-blue hover:text-cnib-blue">
              Ver todas
              <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
            </Link>
          </Reveal>
        </div>

        <div className="mt-10 grid gap-6 md:grid-cols-3" data-testid="home-trainings-grid">
          {items.slice(0, 3).map((t, i) => {
            const primary = t.primary_color || '#F5831F';
            const minPrice = (t.prices || []).reduce((min, p) => p.amount_usd > 0 && (min == null || p.amount_usd < min) ? p.amount_usd : min, null);
            return (
              <Reveal key={t.training_id} delay={i * 0.08}>
                <Link to={`/capacitacion/${t.slug}`} data-testid={`home-training-card-${t.slug}`} className="group block h-full overflow-hidden rounded-3xl bg-white shadow-sm ring-1 ring-slate-100 transition-all duration-300 hover:-translate-y-1 hover:shadow-xl">
                  <div className="relative aspect-[16/10] overflow-hidden bg-slate-100">
                    {t.cover_image ? (
                      <img src={t.cover_image} alt="" className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" />
                    ) : (
                      <div className="h-full w-full" style={{ background: `linear-gradient(135deg, ${primary}, ${t.accent_color || '#0F4C81'})` }} />
                    )}
                    <div className="absolute inset-0 bg-gradient-to-t from-slate-900/60 via-transparent to-transparent" />
                    {t.eyebrow && (
                      <span className="absolute left-3 top-3 rounded-full px-3 py-1 text-[10px] font-bold uppercase tracking-wider text-white" style={{ background: primary }}>
                        {t.eyebrow}
                      </span>
                    )}
                  </div>
                  <div className="p-5">
                    <h3 className="line-clamp-2 font-heading text-lg font-extrabold text-slate-900 group-hover:text-cnib-blue">{t.title}</h3>
                    {t.subtitle && <p className="mt-1 line-clamp-1 text-sm text-slate-500">{t.subtitle}</p>}
                    <div className="mt-3 flex flex-wrap gap-x-3 gap-y-1 text-xs text-slate-500">
                      {t.starts_at && <span>📅 {new Date(t.starts_at).toLocaleDateString('es-BO', { day: '2-digit', month: 'short' })}</span>}
                      <span>🎥 {MODALITY_LABEL[t.modality] || t.modality}</span>
                      {t.duration && <span>⏱️ {t.duration}</span>}
                    </div>
                    <div className="mt-4 flex items-center justify-between">
                      {minPrice != null ? (
                        <p className="font-heading text-xl font-extrabold text-slate-900">
                          <span className="text-[10px] font-bold uppercase tracking-wider text-slate-400">Desde </span>
                          US$ {minPrice}
                        </p>
                      ) : <span />}
                      <span className="inline-flex items-center gap-1 text-sm font-bold" style={{ color: primary }}>
                        Ver detalle <ArrowRight className="h-3 w-3 transition-transform group-hover:translate-x-1" />
                      </span>
                    </div>
                  </div>
                </Link>
              </Reveal>
            );
          })}
        </div>
      </div>
    </section>
  );
}

const SECTION_RENDERERS = {
  hero: Hero,
  marquee: Marquee,
  stats: StatsBar,
  benefits: Benefits,
  connect: Connect,
  shorts: ShortsSection,
  modules: Modules,
  events: EventsSection,
  trainings: TrainingsSection,
  news: NewsSection,
  cta: CtaBanner,
};

function ShortsSection({ cfg }) {
  return <ShortsReel config={cfg.shorts || {}} />;
}

export default function Home() {
  const cfg = useSiteConfig();
  const sections = (cfg.sections || []).filter((s) => s.visible);
  return (
    <div>
      {sections.map((s) => {
        const Renderer = SECTION_RENDERERS[s.id];
        return Renderer ? (
          <Fragment key={s.id}>
            {(s.id === 'marquee' || s.id === 'events' || s.id === 'trainings') ? <Renderer /> : <Renderer cfg={cfg} />}
          </Fragment>
        ) : null;
      })}
    </div>
  );
}
